import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger, useGSAP);

export { gsap, ScrollTrigger, useGSAP };

/** One place to ask, so a component never spells the query wrong. */
export const reduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Anything that MEASURES text has to wait for the display face.
 *
 * Nothing here splits text any more — the words are rendered pre-split from
 * the data file — but layout still shifts when Unbounded swaps in, and a
 * ScrollTrigger built against fallback metrics starts at the wrong scroll
 * position. That is the "first load looks wrong, refresh is fine" bug, and it
 * is invisible on a warm cache.
 */
export function whenFontsReady() {
  if (typeof document === "undefined" || !document.fonts) return Promise.resolve();
  return document.fonts.ready;
}

/**
 * The sunrise: the homepage opens at night even in the light theme, and turns
 * over once you are past the hero.
 *
 * Three bugs are fixed here that made the earlier version feel broken.
 *
 *   - **It ran on every route.** On /projects there is no dark hero to open
 *     with, so the page loaded black and then flipped to paper 60% of a screen
 *     later, for no reason a reader could see. It is now homepage-only.
 *   - **It out-voted the toggle.** Pressing "light" while at the top set --t
 *     back to 0, so the button appeared to do nothing at all. An explicit
 *     choice now ends the effect for the rest of the session: the toggle is
 *     the user telling you what they want, and it wins.
 *   - **It chattered at the boundary.** One threshold means scrolling a few
 *     pixels either side of it flips the whole palette back and forth. There
 *     are two now, 20% of a viewport apart, so a flip can only be undone by
 *     deliberately scrolling back.
 *
 * The turn itself is 140ms on --t. Every colour token is a color-mix() of that
 * one number, so the whole palette crosses in a single interpolation instead
 * of thirty transitions racing each other — which is what made it look ragged.
 *
 * Returns a cleanup function.
 */
export function sunrise(getTheme, { active = true } = {}) {
  const root = document.documentElement;
  let raf = 0;
  let lit = null;

  const teardown = () => {
    window.removeEventListener("scroll", onScroll);
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    root.style.removeProperty("--t");
    root.style.removeProperty("transition");
  };

  const set = (on) => {
    if (on === lit) return;
    lit = on;
    root.style.setProperty("--t", on ? "1" : "0");
  };

  /* Two thresholds, not one. Cross the far one to turn on, the near one to
     turn off; between them nothing happens. */
  const decide = () => {
    const y = window.scrollY;
    const h = window.innerHeight;
    if (lit && y < h * 0.35) return false;
    if (!lit && y > h * 0.55) return true;
    return lit ?? y > h * 0.55;
  };

  function onScroll() {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      set(decide());
    });
  }

  if (!active || getTheme() !== "light") {
    /* Night, a subpage, or the reader has already chosen: leave the
       stylesheet's own --t alone. */
    return teardown;
  }

  if (reduced()) {
    /* Motion off: daylight immediately, never tied to a scroll position. */
    root.style.setProperty("--t", "1");
    return teardown;
  }

  root.style.setProperty("transition", "--t 140ms cubic-bezier(0.3, 0, 0.2, 1)");
  set(decide());
  window.addEventListener("scroll", onScroll, { passive: true });

  return teardown;
}
